import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnChanges,
} from "@angular/core";
import { ClientSideLineTrackingListDto } from "../../../core/models/common/clientSideLineTrackingList.dto";
import { ProductionWorkflowLineTrackingDto } from "../../../core/models/project-workflow/productionWorkflowLineTrackingDto";
import { SaveTrackingListEventDto } from "../../../core/models/project-workflow/saveTrackingListEventDto";
import { SharedService } from "../../../core/services/shared.service";
import { ProjectWorkflowService } from "../../../core/services/project-workflow.service";
import { JniToastrService } from "../../../core/services/jni-toastr.service";
import { FormsModule } from "@angular/forms";
import { CommonModule } from "@angular/common";
import { TrackingListComponent } from "../tracking-list/tracking-list.component";
import { ButtonModule } from "primeng/button";
import { CheckboxModule } from "primeng/checkbox";
import { RadioButtonModule } from "primeng/radiobutton";
import { SelectModule } from "primeng/select";

@Component({
  selector: "jni-add-tracking",
  templateUrl: "./add-tracking.component.html",
  styleUrls: ["./add-tracking.component.scss"],
  imports: [
    FormsModule,
    CommonModule,
    TrackingListComponent,
    ButtonModule,
    CheckboxModule,
    RadioButtonModule,
    SelectModule,
  ],
})
export class AddTrackingComponent implements OnInit, OnChanges {
  @Input() workflowTrackingList = [] as ProductionWorkflowLineTrackingDto[];
  private _totalLineQuantity = 0;

  @Input()
  get totalLineQuantity(): number {
    return this._totalLineQuantity;
  }
  set totalLineQuantity(value: number) {
    this._totalLineQuantity = Number.isFinite(value) ? Number(value) : 0;
    this.syncLineQuantityFromInput();
  }

  @Input() vendorSKU = "";
  @Input() shippingMethod = "";
  @Input() shippingMethodName = "";
  @Input() lineRecIdD365 = 0;
  @Input() addTrackingPurchaseOrder = "";
  @Input() addTrackingLineNumber = 0;
  @Output() saveTrackingListEvent =
    new EventEmitter<SaveTrackingListEventDto>();
  @Output() closeModalDialogEvent = new EventEmitter<boolean>();
  @Output() deleteTrackingRecordEvent =
    new EventEmitter<ClientSideLineTrackingListDto>();

  quantity = 0;
  lineQuantity = 0;
  trackingNumber = "";
  requestList: ClientSideLineTrackingListDto[] = [];
  deliveryStatus = "pending";
  saveButtonEnabled = false;
  pageTrackingList = [] as ClientSideLineTrackingListDto[];
  lineCount = 0;
  isMasterTracking = false;
  trackingType: "fedex" | "other" = "fedex";
  otherShippingMethod = "";
  otherShippingMethods = [] as { code: string; shippingMethod: string }[];
  private otherMethodsLoaded = false;
  constructor(
    private _toastr: JniToastrService,
    private _sharedService: SharedService,
    private _projectWorkflowService: ProjectWorkflowService
  ) {}

  ngOnInit() {
    this.requestList = [];
    this.pageTrackingList = [] as ClientSideLineTrackingListDto[];
    this.trackingType = "fedex";
    this.checkSaveButtonEnabled();
    this.clearData();
    this.syncLineQuantityFromInput();
  }

  ngOnChanges() {
    this.mapTrackingListToModelTrackingList();
    this.syncLineQuantityFromInput();
  }

  onTrackingTypeChange() {
    this.clearData();
    if (this.trackingType === "other" && !this.otherMethodsLoaded) {
      this._projectWorkflowService
        .getManualTrackingShippingMethods()
        .subscribe({
          next: res => {
            this.otherShippingMethods = res?.data ?? [];
            this.otherMethodsLoaded = true;
          },
        });
    }
  }

  addTracking() {
    const isOther = this.trackingType === "other";
    if (isOther && !this.otherShippingMethod) {
      this._toastr.error("Error", "Select a Shipping Method");
      return;
    }

    if (!this.trackingNumber.trim()) {
      this._toastr.error("Error", "Enter Tracking Number");
      return;
    }

    const reqQuantity = this.pageTrackingList.reduce(
      (acc, cur) => acc + cur.quantity,
      0
    );
    if (!isOther && reqQuantity + this.lineQuantity > this.totalLineQuantity) {
      this._toastr.error(
        "Error",
        `Tracking Quantity excceds the Line item Quantity (${this.totalLineQuantity})`
      );
      return;
    }

    const trackingIsExist = this.pageTrackingList.findIndex(
      x => x.trackingNumber === this.trackingNumber
    );
    if (trackingIsExist != -1) {
      this._toastr.error("Error", `TrackingNumber already exist`);
      return;
    }

    const shippedTrackings = this.pageTrackingList.find(
      tracking =>
        tracking.deliveryStatus === "Shipped" ||
        tracking.deliveryStatus === "Delivered"
    );
    if (shippedTrackings) {
      this._toastr.error(
        "Error",
        `Unable to add since other trackings are already shipped or delivered.`
      );
      return;
    }

    this.pageTrackingList.push({
      id: 0,
      vendorSKU: this.vendorSKU,
      quantity: isOther ? 0 : this.lineQuantity,
      trackingNumber: this.trackingNumber,
      deliveryStatus: "Pending",
      isFromDatabase: false,
      shippingMethod: isOther ? this.otherShippingMethod : this.shippingMethod,
      lineRecIdD365: this.lineRecIdD365,
      shippingMethodName: isOther
        ? (this.otherShippingMethods.find(
            x => x.code === this.otherShippingMethod
          )?.shippingMethod ?? this.otherShippingMethod)
        : this.shippingMethodName,
      isMasterTracking: isOther ? false : this.isMasterTracking,
      isManualTracking: isOther,
      deliveryStatusId: 0,
    });

    this.checkSaveButtonEnabled();
    this.clearData();
  }

  saveTrackingList() {
    const requestList = this.pageTrackingList
      .filter(x => x.id === 0)
      .map(obj => {
        return {
          ShippingMethod: obj.isManualTracking ? obj.shippingMethod : "",
          Quantity: obj.quantity,
          TrackingNumber: obj.trackingNumber,
          LineNumber: this.addTrackingLineNumber,
          PurchaseOrder: this.addTrackingPurchaseOrder ?? "",
          VendorSKU: this.vendorSKU,
          IsMasterTracking: obj.isMasterTracking ?? false,
          IsManualTracking: obj.isManualTracking ?? false,
        };
      });
    this.saveTrackingListEvent.emit({
      trackingList: requestList,
      isManualTracking: requestList.some(x => x.IsManualTracking),
    });
    this.requestList = [];
    this.lineCount = 0;
  }

  closeModalDialog() {
    this.trackingType = "fedex";
    this.clearData();
    this.closeModalDialogEvent.emit(false);
  }

  checkSaveButtonEnabled() {
    if (this.pageTrackingList.filter(x => x.id === 0).length > 0) {
      this.saveButtonEnabled = true;
    } else {
      this.saveButtonEnabled = false;
    }
  }

  mapTrackingListToModelTrackingList() {
    this.pageTrackingList = this.workflowTrackingList.map(obj => {
      const tracking = {} as ClientSideLineTrackingListDto;
      tracking.vendorSKU = this.vendorSKU;
      tracking.quantity = obj.quantity;
      tracking.trackingNumber = obj.trackingNumber;
      tracking.deliveryStatus = obj.deliveryStatus;
      tracking.isFromDatabase = true;
      tracking.shippingMethod = obj.shippingMethod;
      tracking.id = obj.trackingLineId;
      tracking.lineRecIdD365 = this.lineRecIdD365;
      tracking.shippingMethodName =
        obj.shippingMethodName && obj.shippingMethodName !== ""
          ? obj.shippingMethodName
          : this.shippingMethodName;
      tracking.isMasterTracking = obj.isMasterTracking;
      tracking.masterTrackingNumber = obj.masterTrackingNumber;
      tracking.carrierShippingMethod = obj.carrierShippingMethod;
      tracking.isManualTracking = obj.isManualTracking ?? false;

      if (obj.isManualTracking) {
        tracking.deliveryStatusId = obj.deliveryStatusId ?? 0;
      } else {
        tracking.deliveryStatusId = 0;
      }

      return tracking;
    });

    this.pageTrackingList = this._sharedService.sortTrackingData(
      this.pageTrackingList
    );
  }

  deleteTrackingRecord(event: ClientSideLineTrackingListDto) {
    this.pageTrackingList = this.pageTrackingList.filter(
      item => item !== event
    );
    this.deleteTrackingRecordEvent.emit(event);
  }

  deleteRow(event: ClientSideLineTrackingListDto) {
    this.pageTrackingList = this.pageTrackingList.filter(
      item => item !== event
    );
  }

  clearData() {
    this.isMasterTracking = false;
    this.trackingNumber = "";
    this.otherShippingMethod = "";
  }

  private syncLineQuantityFromInput(): void {
    this.lineQuantity = this.getDefaultLineQuantity();
  }

  private getDefaultLineQuantity(): number {
    return Number.isFinite(this._totalLineQuantity)
      ? Number(this._totalLineQuantity)
      : 0;
  }

  onMasterTrackingCheckChange() {
    this.isMasterTracking = !this.isMasterTracking;
  }
}
